import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Minus, Plus, Trash2, Pencil, Save, X } from "lucide-react";
import { cacheDrop } from "@/lib/cache";
import { api, apiError, formatDate, money, moneyExact } from "@/lib/api";
import { useConfirm } from "@/components/Confirm";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Image as ImageIcon } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ItemCode, StatusBadge, Spinner } from "@/components/Bits";
import { cn } from "@/lib/utils";

/** The four numbers that produce current stock, shown in the order they apply. */
function LedgerStrip({ item }) {
  const steps = [
    { label: "Opening", value: item.opening_qty, sign: "" },
    { label: "Purchased", value: item.purchased_qty, sign: "+" },
    { label: "Sold", value: item.sold_qty, sign: "−" },
    { label: "In boxes", value: item.giftbox_qty, sign: "−" },
    { label: "Adjusted", value: item.base_qty_used, sign: "−" },
  ];
  return (
    <div className="rounded-xl border border-line bg-surface/70 p-4">
      <div className="eyebrow mb-3">How this count is worked out</div>
      <div className="flex flex-wrap items-end gap-x-1 gap-y-3">
        {steps.map((s, idx) => (
          <div key={s.label} className="flex items-end gap-1">
            {idx > 0 && (
              <span className="font-mono text-muted2 text-sm pb-1.5 px-0.5">{s.sign}</span>
            )}
            <div className="text-center px-1.5">
              <div className="font-mono text-[17px] font-medium text-ink tnum leading-none">
                {s.value ?? 0}
              </div>
              <div className="text-[10.5px] text-muted2 mt-1.5 whitespace-nowrap">{s.label}</div>
            </div>
          </div>
        ))}
        <div className="flex items-end gap-1">
          <span className="font-mono text-muted2 text-sm pb-1.5 px-0.5">=</span>
          <div className="text-center px-1.5">
            <div
              className={cn(
                "font-mono text-[17px] font-semibold tnum leading-none",
                item.current_stock <= 0 ? "text-stock-out" : "text-brand"
              )}
            >
              {item.current_stock}
            </div>
            <div className="text-[10.5px] text-brand mt-1.5 font-medium">On hand</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Where the units went.
 *
 * Sold on their own and packed into boxes are recorded separately, so without
 * this you'd add up two lists by hand. An item that looks slow in Sales can be
 * moving steadily inside gift boxes.
 */
function WhereItWent({ item, sym, boxesOn, boxLabel }) {
  const m = item.movement;
  if (!m || !m.off_the_shelf) return null;

  const word = boxLabel.toLowerCase();

  return (
    <div className="rounded-xl border border-line bg-surface/70 p-4">
      <div className="eyebrow mb-3">Where it went</div>
      <div className="space-y-2">
        <div className="flex items-center gap-3 text-[13px]">
          <span className="text-muted2 flex-1">Sold on their own</span>
          <span className="font-mono text-ink tnum">{m.sold_alone}</span>
        </div>

        {boxesOn && m.packed_into_boxes > 0 && (
          <>
            <div className="flex items-center gap-3 text-[13px]">
              <span className="text-muted2 flex-1">In {word} that sold</span>
              <span className="font-mono text-ink tnum">{m.packed_and_sold}</span>
            </div>
            {m.packed_waiting > 0 && (
              <div className="flex items-center gap-3 text-[13px]">
                <span className="text-muted2 flex-1">
                  Packed, waiting to sell
                  {/* Off the shelf but still yours — worth saying plainly. */}
                </span>
                <span className="font-mono text-stock-low tnum">{m.packed_waiting}</span>
              </div>
            )}
          </>
        )}

        {m.adjusted > 0 && (
          <div className="flex items-center gap-3 text-[13px]">
            <span className="text-muted2 flex-1">Adjusted out</span>
            <span className="font-mono text-ink tnum">{m.adjusted}</span>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2 border-t border-line text-[13px]">
          <span className="text-ink font-medium flex-1">Off the shelf altogether</span>
          <span className="font-mono font-semibold text-brand tnum">{m.off_the_shelf}</span>
        </div>

        {m.packed_waiting > 0 && (
          <p className="text-[11.5px] text-muted2 pt-1 leading-relaxed">
            {m.packed_waiting} of those {m.packed_waiting === 1 ? "is" : "are"} packed
            into {word} nobody's paid for yet — off the shelf, but still yours.
          </p>
        )}

        {m.revenue_alone > 0 && (
          <p className="text-[11.5px] text-muted2 pt-1 leading-relaxed">
            {money(m.revenue_alone, sym)} from selling them individually. What a box
            fetched is counted against the box, not the items in it.
          </p>
        )}
      </div>
    </div>
  );
}

function MovementTable({ rows, columns, empty }) {
  if (!rows?.length) {
    return <div className="py-10 text-center text-[13px] text-muted2">{empty}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-line">
            {columns.map((c) => (
              <th key={c.key} className={cn("t-head px-3 py-2", c.align === "right" && "text-right")}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-line/60 last:border-0">
              {columns.map((c) => (
                <td
                  key={c.key}
                  className={cn("px-3 py-2.5 text-[13px] text-ink", c.align === "right" && "num")}
                >
                  {c.render ? c.render(r) : r[c.key] ?? "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function ItemDrawer({ itemCode, open, onOpenChange, onChanged, onRecoded }) {
  const confirm = useConfirm();
  const { settings, categories, isOn, labelFor } = useSettings();
  const { user } = useAuth();
  const sym = settings.currency_symbol;
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [renumber, setRenumber] = useState(true);
  const [photoReady, setPhotoReady] = useState(false);
  const [uploading, setUploading] = useState(false);
  const photoInput = useRef(null);
  const [previewCode, setPreviewCode] = useState("");
  const [draft, setDraft] = useState({});
  const [adjust, setAdjust] = useState({ delta: "", reason: "" });

  // An item's code already carries its category prefix, so when the category
  // itself is blank we can suggest the right one rather than making them guess.
  const suggestedCategory = useMemo(() => {
    const parts = String(itemCode || "").split("-");
    if (parts.length < 3) return "";
    const prefix = parts[1].toUpperCase();
    return categories.find((c) => c.prefix?.toUpperCase() === prefix)?.name || "";
  }, [itemCode, categories]);

  const load = useCallback(async () => {
    if (!itemCode) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/items/${itemCode}`);
      setItem(data);
      setDraft({
        name: data.name,
        category: data.category,
        unit_cost: data.unit_cost,
        selling_price: data.selling_price,
        opening_qty: data.opening_qty,
        notes: data.notes || "",
      });
      return data;
    } catch (err) {
      toast.error(apiError(err));
      return null;
    } finally {
      setLoading(false);
    }
  }, [itemCode]);

  useEffect(() => {
    // Hide uploading altogether when storage isn't set up, rather than
    // offering something that can only fail.
    api
      .get("/photos/ready")
      .then(({ data }) => setPhotoReady(!!data.ready))
      .catch(() => setPhotoReady(false));
  }, []);

  useEffect(() => {
    if (open) {
      setEditing(false);
      setAdjust({ delta: "", reason: "" });
      load();
    }
  }, [open, load]);

  const save = async () => {
    try {
      const { data } = await api.put(`/items/${itemCode}`, {
        ...draft,
        unit_cost: Number(draft.unit_cost),
        selling_price: Number(draft.selling_price),
        opening_qty: Number(draft.opening_qty),
        renumber: draft.category !== item.category ? renumber : undefined,
      });
      toast.success(
        data.previous_code ? `Now ${data.item_code}` : "Item updated"
      );
      // The name is shown on purchases, sales and boxes, so those pages can't
      // keep serving what they cached before the change.
      ["purchases", "sales", "giftboxes", "reorder", "valuation"].forEach(cacheDrop);
      setEditing(false);
      if (data.previous_code) {
        // The drawer is opened by code, so a renumbered item has to be
        // reopened under its new one — otherwise it reloads a code that no
        // longer exists and shows nothing.
        setItem(data);
        onRecoded?.(data.previous_code, data.item_code);
      } else {
        await load();
      }
      // Pass the updated item back so the list can patch that one row rather
      // than refetching everything and losing the reader's place.
      onChanged?.(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const applyAdjustment = async (direction) => {
    const magnitude = Math.abs(Number(adjust.delta));
    if (!magnitude) {
      toast.error("Enter how many units to add or remove");
      return;
    }
    try {
      await api.post(`/items/${itemCode}/adjust`, {
        delta: direction * magnitude,
        reason: adjust.reason,
      });
      toast.success(`Stock ${direction > 0 ? "increased" : "reduced"} by ${magnitude}`);
      setAdjust({ delta: "", reason: "" });
      const fresh = await load();
      onChanged?.(fresh);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${item.name}?`,
      body: "This can't be undone.",
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/items/${itemCode}`);
      toast.success("Item deleted");
      onOpenChange(false);
      onChanged?.(); // no item passed — the list should refetch
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[620px] overflow-y-auto p-0">
        {loading || !item ? (
          <div className="h-full flex items-center justify-center">
            <Spinner className="w-6 h-6" />
          </div>
        ) : (
          <>
            <div className="px-6 pt-6 pb-5 border-b border-line">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <ItemCode code={item.item_code} size="lg" />
                  <h2 className="font-display text-[22px] font-semibold tracking-[-0.01em] text-ink mt-1.5 leading-tight">
                    {item.name}
                  </h2>
                  <div className="flex items-center gap-2.5 mt-2.5">
                    <StatusBadge status={item.status} />
                    {item.category ? (
                      <span className="text-[12.5px] text-muted2">{item.category}</span>
                    ) : (
                      <button
                        onClick={() => setEditing(true)}
                        className="text-[12.5px] text-stock-low hover:text-gold underline underline-offset-4 decoration-gold/40"
                      >
                        No category — set one
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {editing ? (
                    <>
                      <Button size="sm" onClick={save} className="bg-brand hover:bg-brand-hover">
                        <Save className="w-3.5 h-3.5 mr-1.5" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (!draft.category && suggestedCategory) {
                            setDraft((d) => ({ ...d, category: suggestedCategory }));
                          }
                          setEditing(true);
                        }}
                      >
                        <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                      </Button>
                      {["owner", "admin"].includes(user?.role) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={remove}
                          className="text-muted2 hover:text-stock-out"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <LedgerStrip item={item} />

              {settings.shop?.enabled && photoReady && (
        <div className="rounded-xl border border-line p-4">
          <div className="flex items-start gap-3">
            <div className="w-16 h-16 rounded-xl border border-line bg-surface overflow-hidden shrink-0 flex items-center justify-center">
              {item.photo_url ? (
                <img src={item.photo_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <ImageIcon className="w-5 h-5 text-muted2" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-medium text-ink">Photo</div>
              <p className="text-[12px] text-muted2 mt-0.5 leading-relaxed">
                Shown in the shop. A square photo works best, under 5MB.
              </p>
              <input
                ref={photoInput}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  setUploading(true);
                  try {
                    const body = new FormData();
                    body.append("file", file);
                    const { data } = await api.post(`/items/${itemCode}/photo`, body);
                    setItem(data);
                    onChanged?.(data);
                    toast.success("Photo added");
                  } catch (err) {
                    toast.error(apiError(err));
                  } finally {
                    setUploading(false);
                  }
                }}
              />
              <div className="flex gap-1.5 mt-2.5">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={uploading}
                  onClick={() => photoInput.current?.click()}
                >
                  {uploading ? "Uploading…" : item.photo_url ? "Change" : "Add a photo"}
                </Button>
                {item.photo_url && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-muted2 hover:text-stock-out"
                    onClick={async () => {
                      try {
                        const { data } = await api.delete(`/items/${itemCode}/photo`);
                        setItem(data);
                        onChanged?.(data);
                        toast.success("Photo removed");
                      } catch (err) {
                        toast.error(apiError(err));
                      }
                    }}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {settings.shop?.enabled && (
        <div className="rounded-xl border border-line p-4 flex items-start gap-3">
          <Switch
            checked={!!item.in_shop}
            onCheckedChange={async (v) => {
              try {
                const { data } = await api.put(`/items/${itemCode}`, { in_shop: v });
                setItem(data);
                onChanged?.(data);
                toast.success(v ? "Added to your shop" : "Taken out of your shop");
              } catch (err) {
                toast.error(apiError(err));
              }
            }}
            className="mt-0.5 shrink-0"
          />
          <div className="min-w-0">
            <div className="text-[13.5px] font-medium text-ink">Show in the shop</div>
            <p className="text-[12px] text-muted2 mt-0.5 leading-relaxed">
              Customers see the name, price and whether it's available — never what
              it cost you or how many you have.
            </p>
          </div>
        </div>
      )}

      {settings.shop?.enabled && item.in_shop && (
        <div className="rounded-xl border border-line p-4 space-y-2">
          <Label htmlFor="idesc">Description</Label>
          <Textarea
            id="idesc"
            rows={3}
            defaultValue={item.description || ""}
            onBlur={async (e) => {
              const value = e.target.value;
              if (value === (item.description || "")) return;
              try {
                const { data } = await api.put(`/items/${itemCode}`, { description: value });
                setItem(data);
                onChanged?.(data);
                toast.success("Description saved");
              } catch (err) {
                toast.error(apiError(err));
              }
            }}
            placeholder="What's in it, how big it is, who it suits. Shown only in the shop."
          />
          <p className="text-[12px] text-muted2">Saved when you click away.</p>
        </div>
      )}

      <WhereItWent
                item={item}
                sym={sym}
                boxesOn={isOn("gift_boxes")}
                boxLabel={labelFor("gift_boxes")}
              />

              {editing ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Name</Label>
                    <Input
                      value={draft.name}
                      onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label>Category</Label>
                    <Select
                      value={draft.category || ""}
                      onValueChange={(v) => {
                        setDraft({ ...draft, category: v });
                        setPreviewCode("");
                        // Ask what the code would become, so the choice below
                        // shows the actual new code rather than a guess.
                        if (v && v !== item.category) {
                          api
                            .get(`/items/${itemCode}/next-code`, { params: { category: v } })
                            .then(({ data }) => setPreviewCode(data.code))
                            .catch(() => setPreviewCode(""));
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Not set — choose one" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.category_id || c.name} value={c.name}>
                            <span className="font-mono text-gold mr-2">{c.prefix}</span>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {/* Moving category leaves the code naming the old one,
                        which reads as a mistake. Same choice as merging. */}
                    {draft.category && draft.category !== item.category && (
                      <div className="rounded-xl border border-line p-3 mt-2 space-y-2">
                        <div className="text-[12.5px] text-ink">
                          Moving to {draft.category}
                        </div>
                        <label className="flex items-start gap-2.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={renumber}
                            onChange={(e) => setRenumber(e.target.checked)}
                            className="mt-0.5 accent-[#8E2A5B]"
                          />
                          <span className="text-[12px] text-muted2 leading-relaxed">
                            Renumber the code to match
                            {previewCode && (
                              <>
                                {" — "}
                                <span className="font-mono text-ink">{item.item_code}</span>
                                {" becomes "}
                                <span className="font-mono text-brand">{previewCode}</span>
                              </>
                            )}
                            . Its purchases, sales and gift box entries move with it.
                          </span>
                        </label>
                        {!renumber && (
                          <p className="text-[11.5px] text-muted2">
                            Left as it is, the code keeps the old category's letters.
                          </p>
                        )}
                      </div>
                    )}
                    {!draft.category && (
                      <p className="text-[11.5px] text-muted2">
                        This item has no category. The code suggests{" "}
                        <span className="font-medium text-ink">
                          {suggestedCategory || "none"}
                        </span>
                        .
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Unit cost</Label>
                    <Input
                      type="number"
                      value={draft.unit_cost}
                      onChange={(e) => setDraft({ ...draft, unit_cost: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Selling price</Label>
                    <Input
                      type="number"
                      value={draft.selling_price}
                      onChange={(e) => setDraft({ ...draft, selling_price: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Opening quantity</Label>
                    <Input
                      type="number"
                      value={draft.opening_qty}
                      onChange={(e) => setDraft({ ...draft, opening_qty: e.target.value })}
                    />
                    <p className="text-[11.5px] text-muted2">
                      What you started with, before any logged purchase.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Notes</Label>
                    <Input
                      value={draft.notes}
                      onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-line rounded-xl overflow-hidden border border-line">
                  {[
                    { label: "Unit cost", value: money(item.unit_cost, sym) },
                    { label: "Sells for", value: money(item.selling_price, sym) },
                    { label: "Stock value", value: money(item.stock_value, sym) },
                    { label: "At retail", value: money(item.potential_value, sym) },
                  ].map((c) => (
                    <div key={c.label} className="bg-white px-3.5 py-3">
                      <div className="text-[10.5px] uppercase tracking-[0.1em] text-muted2 font-mono">
                        {c.label}
                      </div>
                      <div className="text-[15px] font-medium text-ink mt-1 tnum">{c.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {item.notes && !editing && (
                <p className="text-[13px] text-muted2 leading-relaxed">{item.notes}</p>
              )}

              <div className="rounded-xl border border-line p-4">
                <div className="eyebrow">Correct the count</div>
                <p className="text-[12.5px] text-muted2 mt-1.5">
                  For breakage, a miscount or stock taken for personal use. The change
                  stays visible in the ledger above.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 mt-3">
                  <Input
                    type="number"
                    min="1"
                    placeholder="Units"
                    value={adjust.delta}
                    onChange={(e) => setAdjust({ ...adjust, delta: e.target.value })}
                    className="sm:w-24"
                  />
                  <Input
                    placeholder="Reason (optional)"
                    value={adjust.reason}
                    onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })}
                    className="flex-1"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => applyAdjustment(1)} title="Add units">
                      <Plus className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" onClick={() => applyAdjustment(-1)} title="Remove units">
                      <Minus className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <Tabs defaultValue="purchases">
                <TabsList className="bg-brand-light/60">
                  <TabsTrigger value="purchases">
                    Purchases {item.purchases?.length ? `(${item.purchases.length})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="sales">
                    Sales {item.sales?.length ? `(${item.sales.length})` : ""}
                  </TabsTrigger>
                  <TabsTrigger value="boxes">
                    Gift boxes {item.gift_boxes?.length ? `(${item.gift_boxes.length})` : ""}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="purchases" className="mt-3">
                  <MovementTable
                    rows={item.purchases}
                    empty="No purchases logged for this item yet."
                    columns={[
                      { key: "purchase_date", label: "Date", render: (r) => formatDate(r.purchase_date) },
                      { key: "supplier", label: "Supplier", render: (r) => r.supplier || "—" },
                      { key: "quantity", label: "Qty", align: "right" },
                      {
                        key: "total_cost",
                        label: "Cost",
                        align: "right",
                        render: (r) => moneyExact(r.total_cost, sym),
                      },
                    ]}
                  />
                </TabsContent>

                <TabsContent value="sales" className="mt-3">
                  <MovementTable
                    rows={item.sales}
                    empty="No sales logged for this item yet."
                    columns={[
                      { key: "sale_date", label: "Date", render: (r) => formatDate(r.sale_date) },
                      { key: "customer", label: "Customer", render: (r) => r.customer || "—" },
                      { key: "quantity", label: "Qty", align: "right" },
                      {
                        key: "revenue",
                        label: "Revenue",
                        align: "right",
                        render: (r) => moneyExact(r.revenue, sym),
                      },
                    ]}
                  />
                </TabsContent>

                <TabsContent value="boxes" className="mt-3">
                  <MovementTable
                    rows={item.gift_boxes}
                    empty="This item hasn't gone into a gift box yet."
                    columns={[
                      { key: "build_date", label: "Date", render: (r) => formatDate(r.build_date) },
                      { key: "box_type", label: "Box" },
                      { key: "quantity", label: "Qty", align: "right" },
                    ]}
                  />
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
